import React, { useContext, useEffect, useState } from 'react';
import { Card } from '@bb/elements-web';
import styled from 'styled-components';
import { AuthContext } from 'contexts';
import { useAnsweringServiceDetails } from 'hooks';
import {
  SupraSubscriptionProductBillingModel,
  SupraSystemSubscriptionStatus,
} from '@bb/domain/dist/types';
import { Feature } from '@bb/automaton';
import getOrgBillingModel from 'api/SupraController/getOrgBillingModel';
import CurrentServicesSection from './components/CurrentServicesSection';
import AvailableServicesSection from './components/AvailableServicesSection';
import { PlanTier, OrgPlanPayer } from './components/OrgPlanCard';
import { ViewingLevel } from '../..';

export interface AllSubscriptionsCardProps {
  /** ID of the user viewing this page */
  userId: string;
  /** Whether these are the subscriptions for an indivdual user or for a brokerage paying for all its users */
  viewingLevel: ViewingLevel;
  /** Callback function when the Org Plan (i.e. Tier) button is clicked */
  onOrgPlanClick: () => void;
  /** Callback function when the Answering Service button is clicked */
  onAnsweringServiceClick: () => void;
  /** Callback function when the Supra Subscription button is clicked */
  onSupraOneClick: () => void;
}

/**
 * Main subscriptions card displaying a user or brokerage's current services
 * (i.e. active subscriptions) and available services they can sign up for.
 */
const AllSubscriptionsCard: React.FC<AllSubscriptionsCardProps> = ({
  userId,
  viewingLevel,
  onOrgPlanClick,
  onAnsweringServiceClick,
  onSupraOneClick,
}) => {
  // #region States & variables

  const { auth, hasFeature, isOrgOnBrokeragePlan, isOrgPlanUpgraded } =
    useContext(AuthContext);

  /** Whether the SUPRA_MVP flag is turned on granting access to all Supra One features for this org. */
  const canUseSupraOne = hasFeature(Feature.SUPRA_MVP);

  /** The type of Org Plan this brokerage is on. Agent Plan (Tier 1) or Brokerage Plan (Tier 2). */
  const currentOrgPlan: PlanTier =
    isOrgOnBrokeragePlan || isOrgPlanUpgraded ? 'tier2' : 'tier1';
  /** How the Org Plan subscription is billed */
  const orgPlanPayer: OrgPlanPayer =
    isOrgOnBrokeragePlan && !isOrgPlanUpgraded ? 'boardOrMls' : 'brokerage';

  const {
    answeringServiceType,
    answeringServicePayer,
    hasActiveAnsweringService,
  } = useAnsweringServiceDetails({ viewingLevel, userId });

  // Grab the details about the user's Supra One subscription, if it exists.
  let hasActiveSupraSubscription = !!auth.supraSubscription;
  if (hasActiveSupraSubscription) {
    hasActiveSupraSubscription =
      auth.supraSubscription?.status !==
      SupraSystemSubscriptionStatus.unsubscribed;
  }
  const supraSubscriptionType = hasActiveSupraSubscription
    ? auth.supraSubscription.type
    : null;

  // Go grab who is in charge of paying for the Supra One subscription.
  const [supraSubscriptionPayer, setSupraSubscriptionPayer] =
    useState<SupraSubscriptionProductBillingModel>(null);
  const [fetchErrorSupraOne, setFetchErrorSupraOne] = useState<boolean>(false);
  useEffect(() => {
    const fetchSupraSubscriptionPayer = async () => {
      if (hasActiveSupraSubscription) {
        try {
          setSupraSubscriptionPayer(
            await getOrgBillingModel({ orgId: auth.org.id }),
          );
        } catch {
          setFetchErrorSupraOne(true);
        }
      }
    };
    fetchSupraSubscriptionPayer();
  }, [hasActiveSupraSubscription, auth.org.id]);

  // Set whether we encountered the error during the Answering Service data
  // fetch so we can disable the button to start the Answering Service flow.
  const [fetchErrorAnsweringService, setFetchErrorAnsweringService] =
    useState<boolean>(false);
  useEffect(() => {
    if (!answeringServiceType || !answeringServicePayer) {
      setFetchErrorAnsweringService(true);
    } else {
      setFetchErrorAnsweringService(false);
    }
  }, [answeringServiceType, answeringServicePayer]);
  // #endregion States & variables
  // #region JSX to return

  return (
    <StyledCard dataCy="all-subscriptions-card">
      <ContentContainer>
        <CurrentServicesSection
          answeringServicePayer={answeringServicePayer}
          answeringServiceType={answeringServiceType}
          canUseSupraOne={canUseSupraOne}
          currentOrgPlan={currentOrgPlan}
          fetchErrorAnsweringService={fetchErrorAnsweringService}
          fetchErrorSupraOne={fetchErrorSupraOne}
          hasActiveAnsweringService={hasActiveAnsweringService}
          hasActiveSupraSubscription={hasActiveSupraSubscription}
          onAnsweringServiceClick={onAnsweringServiceClick}
          onOrgPlanClick={onOrgPlanClick}
          onSupraOneClick={onSupraOneClick}
          orgPlanPayer={orgPlanPayer}
          supraSubscriptionPayer={supraSubscriptionPayer}
          supraSubscriptionType={supraSubscriptionType}
          viewingLevel={viewingLevel}
        />
        <AvailableServicesSection
          answeringServicePayer={answeringServicePayer}
          answeringServiceType={answeringServiceType}
          canUseSupraOne={canUseSupraOne}
          fetchErrorAnsweringService={fetchErrorAnsweringService}
          hasActiveAnsweringService={hasActiveAnsweringService}
          hasActiveSupraSubscription={hasActiveSupraSubscription}
          onAnsweringServiceClick={onAnsweringServiceClick}
          viewingLevel={viewingLevel}
        />
      </ContentContainer>
    </StyledCard>
  );
};

// #endregion JSX to return

export default AllSubscriptionsCard;

const StyledCard = styled(Card)`
  .ant-card-body {
    padding: 24px;
  }
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;
